import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SpiderChart from "component/charts/SpiderChart";

import Co2Calculator from "component/charts/Co2Calculator";
import MainChart from "component/charts/MainChart";
import MarketStabilityList from "component/charts/MarketStabilityList.jsx";
import RiskRecycling from "component/charts/RiskRecycling";
import SupplierTab from "component/charts/SupplierTab.jsx";

import ProcessSteps from "component/charts/ProcessSteps.jsx";
import TableAndAverage from "component/charts/TableAndAverage.jsx";
import AvailabilityAndNeed from "component/charts/AvailabilityAndNeed.jsx";
import "../../css/chart-main.css";
import ChartHeader from "component/charts/ChartHeader";
import ComparisonHeader from "component/charts/ComparisonHeader";
import HotNewsComments from "component/news/HotNewsCard.jsx";
import useBreakpoint from "../../utils/useBreakPoint.js";
import { interactionService } from "../../services";
import { useMaterialGeneral, useMaterialCountryProduction, useMaterialMarketStability } from "../../hooks";
import { getMaterialInsights } from "../News/api";
import { SkeletonText, SkeletonBlock } from "component/Loading";

const INSIGHTS_PAGE_SIZE = 10;
const INSIGHTS_SCROLL_THRESHOLD_PX = 160;
const SHOW_SUPPLIER_TAB = false;

const ChartsPage = () => {
  const { t } = useTranslation();
  const { materialId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState("list");
  const [comparisons, setComparisons] = useState([]);
  const [dynamicHeaderMaterial, setDynamicHeaderMaterial] = useState(null);
  // Umgerechneter letzter Kurs je Vergleichsreihe (nur bei aktiver Umrechnung)
  const [comparisonHeaderOverrides, setComparisonHeaderOverrides] = useState({});
  const [comparisonColorMap, setComparisonColorMap] = useState({});
  const [materialInsights, setMaterialInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsLoadingMore, setInsightsLoadingMore] = useState(false);
  const [insightsHasMore, setInsightsHasMore] = useState(true);
  const [insightsOffset, setInsightsOffset] = useState(0);
  const [insightsError, setInsightsError] = useState("");
  const [infoPanelTab, setInfoPanelTab] = useState("sources");
  const insightsScrollRef = useRef(null);
  const insightsRequestInFlightRef = useRef(false);
  const breakpoint = useBreakpoint();

  // Fetch material data to get the numeric ID
  const { data: material } = useMaterialGeneral(materialId);
  const { data: productionData, loading: productionLoading } = useMaterialCountryProduction(materialId);
  const { data: marketStabilityData, loading: marketStabilityLoading } = useMaterialMarketStability(materialId);

  // Drag and drop state for reorderable components
  const [draggableComponents, setDraggableComponents] = useState([
    { id: 'table-average', component: 'TableAndAverage' },
    { id: 'risk-recycling', component: 'RiskRecycling' },
    { id: 'availability-need', component: 'AvailabilityAndNeed' },
    //{ id: 'co2-calculator', component: 'Co2Calculator' }
  ]);

  const [draggedItem, setDraggedItem] = useState(null);
  const hiddenInComparisonIds = new Set(["table-average", "risk-recycling", "availability-need"]);

  const handleComparisonColorsChange = useCallback((nextColorMap = {}) => {
    setComparisonColorMap((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(nextColorMap || {});
      if (prevKeys.length === nextKeys.length) {
        const unchanged = nextKeys.every((key) => (prev || {})[key] === nextColorMap[key]);
        if (unchanged) return prev;
      }
      return nextColorMap;
    });
  }, []);

  const handleComparisonHeaderDataChange = useCallback((next = {}) => {
    // Leer bleibt leer (kein neues Objekt), sonst unnoetige Renderdurchlaeufe
    setComparisonHeaderOverrides((prev) => (
      Object.keys(prev).length === 0 && Object.keys(next || {}).length === 0 ? prev : (next || {})
    ));
  }, []);

  const effectiveComparisons = useMemo(
    () =>
      comparisons.map((comparison) => ({
        ...comparison,
        color: comparisonColorMap[comparison.id] || comparison.color,
      })),
    [comparisons, comparisonColorMap]
  );

  useEffect(() => {
    document.title = "Signality";
  }, []);

  useEffect(() => {
    setDynamicHeaderMaterial(null);
    setComparisonColorMap({});
    setComparisonHeaderOverrides({});
  }, [materialId]);

  // Restore comparisons from navigation state (when swapping main material)
  useEffect(() => {
    if (location.state?.comparisons && Array.isArray(location.state.comparisons)) {
      setComparisons(location.state.comparisons);

      // Clear the state after restoration to avoid re-applying on subsequent renders
      // We use window.history.replaceState to update state without triggering navigation
      window.history.replaceState({
        ...location.state,
        comparisons: undefined
      }, '');
    }
  }, [location.state, materialId]); // Re-run when materialId changes

  // Track material view when material data is loaded
  useEffect(() => {
    if (material && material.id) {
      interactionService.trackMaterialView(material.id);
    }
  }, [material]);

  const loadInsightsPage = useCallback(async (offsetValue, append = false) => {
    if (insightsRequestInFlightRef.current) return;
    insightsRequestInFlightRef.current = true;
    if (append) {
      setInsightsLoadingMore(true);
    } else {
      setInsightsLoading(true);
    }
    try {
      const items = await getMaterialInsights({
        materialCode: materialId,
        limit: INSIGHTS_PAGE_SIZE,
        offset: offsetValue,
      });
      const safeItems = Array.isArray(items) ? items : [];
      if (append) {
        setMaterialInsights((prev) => {
          const existingIds = new Set(prev.map((entry) => entry.id));
          const uniqueIncoming = safeItems.filter((entry) => !existingIds.has(entry.id));
          return [...prev, ...uniqueIncoming];
        });
      } else {
        setMaterialInsights(safeItems);
      }
      setInsightsOffset(offsetValue + safeItems.length);
      setInsightsHasMore(safeItems.length === INSIGHTS_PAGE_SIZE);
      setInsightsError("");
    } catch (e) {
      setInsightsError(e?.message || t("charts.error.loadInsights"));
      if (!append) {
        setMaterialInsights([]);
      }
    } finally {
      if (append) {
        setInsightsLoadingMore(false);
      } else {
        setInsightsLoading(false);
      }
      insightsRequestInFlightRef.current = false;
    }
  }, [materialId, t]);

  useEffect(() => {
    insightsRequestInFlightRef.current = false;
    setInsightsOffset(0);
    setInsightsHasMore(true);
    setInsightsLoadingMore(false);
    setInsightsError("");
    setMaterialInsights([]);
    loadInsightsPage(0, false);
  }, [materialId, loadInsightsPage]);

  const handleInsightsScroll = useCallback(() => {
    const el = insightsScrollRef.current;
    if (!el || insightsLoading || insightsLoadingMore || !insightsHasMore) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining <= INSIGHTS_SCROLL_THRESHOLD_PX) {
      loadInsightsPage(insightsOffset, true);
    }
  }, [insightsLoading, insightsLoadingMore, insightsHasMore, insightsOffset, loadInsightsPage]);

  const hasSourcesData = useMemo(() => {
    const countries = Array.isArray(productionData?.countries) ? productionData.countries : [];
    return countries.some((country) => {
      const value = Number(country?.value);
      const share = Number(country?.share);
      return (Number.isFinite(value) && value > 0) || (Number.isFinite(share) && share > 0);
    });
  }, [productionData]);

  const hasInsightsData = materialInsights.length > 0;
  const showInsightsTab = insightsLoading || insightsLoadingMore || hasInsightsData;
  const showSourcesTab = productionLoading || hasSourcesData;
  const showTopInfoPanel = showSourcesTab || showInsightsTab;

  const hasMarketStabilityData = useMemo(() => {
    if (!Array.isArray(marketStabilityData)) return false;
    return marketStabilityData.some((item) => {
      if (!item) return false;
      const stability = Number(item.stability);
      return Boolean(item.country) && Number.isFinite(stability);
    });
  }, [marketStabilityData]);

  const showMarketPanel = marketStabilityLoading || hasMarketStabilityData || SHOW_SUPPLIER_TAB;

  useEffect(() => {
    if (!showTopInfoPanel) return;
    if (infoPanelTab === "sources" && !showSourcesTab) {
      setInfoPanelTab(showInsightsTab ? "insights" : "sources");
      return;
    }
    if (infoPanelTab === "insights" && !showInsightsTab) {
      setInfoPanelTab(showSourcesTab ? "sources" : "insights");
    }
  }, [infoPanelTab, showTopInfoPanel, showSourcesTab, showInsightsTab]);

  // Handle mouse down to check if drag should be allowed
  const handleMouseDown = (e, index) => {
    // Check if the mouse down happened on or inside the drag-handle
    const dragHandle = e.target.closest('.drag-handle');
    if (!dragHandle) {
      // Prevent dragging if not clicking on drag-handle
      e.preventDefault();
    }
  };

  // Handle adding a new comparison
  const handleAddComparison = (material) => {
    if (comparisons.length >= 3) return; // Max 3 comparisons

    // Check for duplicates by material code
    const isDuplicate = comparisons.some(comp => comp.code === material.code);
    if (isDuplicate) return;

    // Track material view for comparison (fire-and-forget)
    // This makes the compared material appear in "Recently Viewed"
    if (material.id) {
      interactionService.trackMaterialView(material.id);
    }

    const newComparison = {
      id: Date.now(), // Simple ID generation
      code: material.code, // Material code (e.g., "MAT_1")
      name: material.name,
      price: material.price,
      color: getComparisonColor(comparisons.length)
    };

    setComparisons(prev => [...prev, newComparison]);
  };

  // Handle removing a comparison
  const handleRemoveComparison = (id) => {
    setComparisons(prev => prev.filter(comp => comp.id !== id));
  };

  // Handle removing the main material (swap to first comparison)
  const handleRemoveMainMaterial = () => {
    if (comparisons.length === 0) return;

    // Take the first comparison as the new main material
    const newMainMaterial = comparisons[0];

    // Remaining comparisons (shift indices down)
    const remainingComparisons = comparisons.slice(1).map((comp, index) => ({
      ...comp,
      color: getComparisonColor(index) // Reassign colors
    }));

    // Navigate to the new main material's page with remaining comparisons in state
    navigate(`/charts/material/${newMainMaterial.code}`, {
      state: {
        comparisons: remainingComparisons,
        sectorName: location.state?.sectorName
      }
    });
  };

  // Get color for comparison based on index
  const getComparisonColor = (index) => {
    const colors = ['#3B82F6', '#10B981', '#F97316']; // Blue, Green, Orange
    return colors[index] || '#6B7280';
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedItem(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newComponents = [...draggableComponents];
    const draggedComponent = newComponents[draggedItem];
    newComponents.splice(draggedItem, 1);
    newComponents.splice(index, 0, draggedComponent);

    setDraggableComponents(newComponents);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Component map for rendering
  const componentMap = {
    TableAndAverage: <TableAndAverage materialCode={materialId} />,
    RiskRecycling: <RiskRecycling materialCode={materialId} />,
    AvailabilityAndNeed: <AvailabilityAndNeed materialCode={materialId} />,
    //Co2Calculator: <Co2Calculator className="mt-4" />
  };

  const visibleDraggableComponents = comparisons.length > 0
    ? draggableComponents.filter((item) => !hiddenInComparisonIds.has(item.id))
    : draggableComponents;

  return (
    <div className="charts_page">
      <div className="charts_content">
        {/* Central column */}
        <div className="center_column mt-4">
          <ChartHeader
            material={dynamicHeaderMaterial || material}
            hasComparisons={comparisons.length > 0}
            onRemove={handleRemoveMainMaterial}
            color={(dynamicHeaderMaterial && dynamicHeaderMaterial.main_color) || '#8B5CF6'}
          />

          {/* Render comparison headers */}
          {effectiveComparisons.map(comparison => (
            <div key={comparison.id} className="mt-3">
              <ComparisonHeader
                comparison={comparison}
                override={comparisonHeaderOverrides[comparison.code]}
                onRemove={handleRemoveComparison}
              />
            </div>
          ))}
          
          <div className="chart-header-actions">
            {/*<ProcessSteps />*/}
          </div>

          <MainChart
            materialCode={materialId}
            material={material}
            insights={materialInsights}
            comparisons={comparisons}
            onAddComparison={handleAddComparison}
            maxComparisons={3}
            currentMaterialCode={materialId}
            onHeaderDataChange={setDynamicHeaderMaterial}
            onComparisonColorsChange={handleComparisonColorsChange}
            onComparisonHeaderDataChange={handleComparisonHeaderDataChange}
          />

          {/* Draggable components */}
          {visibleDraggableComponents.map((item, index) => (
            <div
              key={item.id}
              draggable
              onMouseDown={(e) => handleMouseDown(e, index)}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`draggable-component ${draggedItem === index ? 'dragging' : ''}`}
            >
              {componentMap[item.component]}
            </div>
          ))}
        </div>

        {/* Right column */}
        <div className="right_column">
          {showTopInfoPanel && (
            <div>
              <div className="news_comments">
              {/* Tabs */}
              <ul className="news-tabs" role="tablist">
                {/*  YOUR FEED (only on tablet & mobile, shown first) */}
                {["tablet", "mobile"].includes(breakpoint) && (
                  <li className="news-tab-item" role="presentation">
                    <button
                      className="news-tab-btn active"
                      id="feed-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#yourFeed"
                      type="button"
                      role="tab"
                      aria-controls="yourFeed"
                      aria-selected="true"
                    >
                      {t("charts.tabs.yourFeed")}
                    </button>
                  </li>
                )}

                {/* HOT NEWS (second) */}
                {showSourcesTab && (
                  <li className="news-tab-item" role="presentation">
                    <button
                      className={`news-tab-btn ${infoPanelTab === "sources" ? "active" : ""}`}
                      id="hot-tab"
                      type="button"
                      role="tab"
                      aria-controls="sources"
                      aria-selected={infoPanelTab === "sources"}
                      onClick={() => setInfoPanelTab("sources")}
                    >
                      {t("charts.tabs.sources")}
                    </button>
                  </li>
                )}

                {/* ALL NEWS (last) */}
                {showInsightsTab && (
                  <li className="news-tab-item" role="presentation">
                    <button
                      className={`news-tab-btn ${infoPanelTab === "insights" ? "active" : ""}`}
                      id="all-tab"
                      type="button"
                      role="tab"
                      aria-controls="insights"
                      aria-selected={infoPanelTab === "insights"}
                      onClick={() => setInfoPanelTab("insights")}
                    >
                      {t("charts.tabs.insights")}
                    </button>
                  </li>
                )}
              </ul>

              {/* Tab Content */}
              <div
                className="news-tab-content tab-content mt-4"
                id="newsTabContent"
              >
                {showSourcesTab && infoPanelTab === "sources" && (
                  <div
                    className="tab-pane fade show active"
                    id="sources"
                    role="tabpanel"
                    aria-labelledby="hot-tab"
                  >
                    <SpiderChart
                      materialCode={materialId}
                      materialName={material?.name}
                      mainColor={(dynamicHeaderMaterial && dynamicHeaderMaterial.main_color) || '#8B5CF6'}
                      comparisons={effectiveComparisons}
                    />
                  </div>
                )}

                {showInsightsTab && infoPanelTab === "insights" && (
                  <div
                    className="tab-pane fade show active"
                    id="insights"
                    role="tabpanel"
                    aria-labelledby="all-tab"
                  >
                    <div
                      ref={insightsScrollRef}
                      onScroll={handleInsightsScroll}
                      style={{
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingRight: "4px",
                      }}
                    >
                      {insightsLoading && (
                        <div className="mt-2 d-flex flex-column gap-3">
                          {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="skeleton-card p-3">
                              <SkeletonBlock width="55%" height={14} style={{ marginBottom: 10 }} />
                              <SkeletonText lines={3} lineHeight={11} gap={7} widths={["100%", "90%", "65%"]} />
                            </div>
                          ))}
                        </div>
                      )}
                      {!insightsLoading && insightsError && (
                        <div className="mt-2 text-danger">{insightsError}</div>
                      )}
                      {!insightsLoading && !insightsError && materialInsights.length === 0 && (
                        <div className="mt-2 text-white-50">{t("charts.insights.none")}</div>
                      )}
                      {!insightsLoading && !insightsError && materialInsights.map((item) => (
                        <div className="mt-4" key={item.id}>
                          <HotNewsComments
                            _id={item.id}
                            title={item.title}
                            summary={item.summary}
                            linkCount={item.link_count}
                            source_logos={item.source_logos}
                            growth_pct={item.growth_pct ?? 0}
                          />
                        </div>
                      ))}
                      {!insightsLoading && insightsLoadingMore && (
                        <div className="mt-3 skeleton-card p-3">
                          <SkeletonText lines={2} lineHeight={11} gap={7} widths={["80%", "55%"]} />
                        </div>
                      )}
                      {!insightsLoading && !insightsHasMore && materialInsights.length > 0 && (
                        <div className="mt-3 text-white-50">{t("charts.insights.upToDate")}</div>
                      )}
                      <div style={{ height: "8px" }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          )}

          {/* Example widgets - removed unused TransparentCard */}

          {showMarketPanel && (
            <div>
              <div className="news_comments mt-4">
              {/* Tabs */}
              <ul className="news-tabs" role="tablist">
                {/* HOT NEWS (second) */}
                <li className="news-tab-item" role="presentation">
                  <button
                    className={`news-tab-btn ${
                      !["tablet", "mobile"].includes(breakpoint) ? "active" : ""
                    }`}
                    id="hot-tab"
                    data-bs-toggle="pill"
                    data-bs-target="#market-stability"
                    type="button"
                    role="tab"
                    aria-controls="market-stability"
                    aria-selected={!["tablet", "mobile"].includes(breakpoint)}
                  >
                    {t("charts.tabs.marketStability")}
                  </button>
                </li>
                {SHOW_SUPPLIER_TAB && (
                  <li className="news-tab-item" role="presentation">
                    <button
                      className="news-tab-btn"
                      id="supplier-tab"
                      data-bs-toggle="pill"
                      data-bs-target="#supplier"
                      type="button"
                      role="tab"
                      aria-controls="supplier"
                      aria-selected="false"
                    >
                      {t("charts.tabs.supplier")}
                    </button>
                  </li>
                )}

              </ul>

              {/* Tab Content */}
              <div
                className="news-tab-content tab-content mt-4"
                id="newsTabContent"
              >
                <div
                  className={`tab-pane fade ${
                    !["tablet", "mobile"].includes(breakpoint)
                      ? "show active"
                      : ""
                  }`}
                  id="market-stability"
                  role="tabpanel"
                  aria-labelledby="hot-tab"
                >
                  <MarketStabilityList
                    materialCode={materialId}
                    data={marketStabilityData}
                    loading={marketStabilityLoading}
                    className="mt-4"
                  />
                </div>
                {SHOW_SUPPLIER_TAB && (
                  <div
                    className="tab-pane fade"
                    id="supplier"
                    role="tabpanel"
                    aria-labelledby="supplier-tab"
                  >
                    <SupplierTab materialCode={materialId} className="mt-4" />
                  </div>
                )}
              </div>
            </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartsPage;
